import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Space,
  message,
  Spin,
  Alert,
  Modal,
  Divider,
  Typography,
  Empty,
  Row,
  Col
} from 'antd';
import { SaveOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { whatsappSettingsService } from '../services/whatsappSettingsService';

const { Title, Text } = Typography;

const WhatsAppSettingsForm = ({ organizationId, onSave }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [whatsappSettings, setWhatsappSettings] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchWhatsappSettings();
  }, [organizationId]);

  const fetchWhatsappSettings = async () => {
    setLoading(true);
    try {
      const response = await whatsappSettingsService.getWhatsappSettings(organizationId);
      if (response.success) {
        setWhatsappSettings(response.data);
        if (response.data && response.data.companyPhone) {
          form.setFieldsValue({
            companyPhone: response.data.companyPhone,
            gallaboxApiKey: response.data.gallaboxApiKey || '',
            gallaboxApiSecret: response.data.gallaboxApiSecret || '',
            gallaboxWhatsappUrl: response.data.gallaboxWhatsappUrl || '',
            userResponseWebhookUrl: response.data.userResponseWebhookUrl || '',
            leadWebhookUrl: response.data.leadWebhookUrl || '',
            whatsappChannelId: response.data.whatsappChannelId || '',
            introductionTemplateId: response.data.introductionTemplateId || ''
          });
          setIsEditing(true);
        }
      }
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
      message.error('Failed to fetch WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (isEditing) {
      // Pre-fill form with existing data
      form.setFieldsValue({
        companyPhone: whatsappSettings?.companyPhone || '',
        gallaboxApiKey: whatsappSettings?.gallaboxApiKey || '',
        gallaboxApiSecret: whatsappSettings?.gallaboxApiSecret || '',
        gallaboxWhatsappUrl: whatsappSettings?.gallaboxWhatsappUrl || '',
        userResponseWebhookUrl: whatsappSettings?.userResponseWebhookUrl || '',
        leadWebhookUrl: whatsappSettings?.leadWebhookUrl || '',
        whatsappChannelId: whatsappSettings?.whatsappChannelId || '',
        introductionTemplateId: whatsappSettings?.introductionTemplateId || ''
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const handleSaveSettings = async (values) => {
    setSaving(true);
    try {
      const response = await whatsappSettingsService.saveWhatsappSettings(organizationId, values);
      if (response.success) {
        message.success(isEditing ? 'WhatsApp settings updated successfully' : 'WhatsApp settings saved successfully');
        setWhatsappSettings(response.data);
        setIsEditing(true);
        setModalVisible(false);
        if (onSave) {
          onSave();
        }
      } else {
        message.error(response.message || 'Failed to save WhatsApp settings');
      }
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
      message.error(error.response?.data?.message || 'Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSettings = () => {
    Modal.confirm({
      title: 'Delete WhatsApp Settings',
      content: 'Are you sure you want to delete all WhatsApp settings? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        try {
          const response = await whatsappSettingsService.deleteWhatsappSettings(organizationId);
          if (response.success) {
            message.success('WhatsApp settings deleted successfully');
            setWhatsappSettings(null);
            setIsEditing(false);
            form.resetFields();
            if (onSave) {
              onSave();
            }
          } else {
            message.error(response.message || 'Failed to delete WhatsApp settings');
          }
        } catch (error) {
          console.error('Error deleting WhatsApp settings:', error);
          message.error('Failed to delete WhatsApp settings');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading WhatsApp settings...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Current Settings Display */}
      {isEditing && whatsappSettings ? (
        <Card
          title={
            <span>
              <Title level={5} style={{ margin: 0 }}>WhatsApp Configuration</Title>
            </span>
          }
          extra={
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleOpenModal}
              >
                Edit
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteSettings}
                loading={saving}
              >
                Delete
              </Button>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Company Phone:</Text>
                <div style={{ color: '#666', marginTop: 4 }}>{whatsappSettings?.companyPhone}</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>WhatsApp Channel ID:</Text>
                <div style={{ color: '#666', marginTop: 4 }}>{whatsappSettings?.whatsappChannelId}</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Gallabox WhatsApp URL:</Text>
                <div style={{ color: '#666', marginTop: 4, wordBreak: 'break-all' }}>{whatsappSettings?.gallaboxWhatsappUrl}</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Introduction Template ID:</Text>
                <div style={{ color: '#666', marginTop: 4 }}>{whatsappSettings?.introductionTemplateId}</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>User Response Webhook URL:</Text>
                <div style={{ color: '#666', marginTop: 4, wordBreak: 'break-all' }}>{whatsappSettings?.userResponseWebhookUrl}</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Lead Webhook URL:</Text>
                <div style={{ color: '#666', marginTop: 4, wordBreak: 'break-all' }}>{whatsappSettings?.leadWebhookUrl}</div>
              </div>
            </Col>
          </Row>
        </Card>
      ) : (
        <Card style={{ marginBottom: 24 }}>
          <Empty
            description="No WhatsApp settings configured"
            style={{ marginBottom: 16 }}
          />
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleOpenModal}
            block
          >
            Add WhatsApp Settings
          </Button>
        </Card>
      )}

      {/* Edit/Create Modal */}
      <Modal
        title={isEditing ? 'Edit WhatsApp Settings' : 'Add WhatsApp Settings'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveSettings}
          autoComplete="off"
        >
          <Alert
            message="All fields are required"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            label="Company Phone"
            name="companyPhone"
            rules={[
              { required: true, message: 'Please enter company phone' },
              { min: 5, message: 'Phone number must be at least 5 characters' }
            ]}
          >
            <Input placeholder="e.g., +1234567890" />
          </Form.Item>

          <Divider>Gallabox Credentials</Divider>

          <Form.Item
            label="Gallabox API Key"
            name="gallaboxApiKey"
            rules={[{ required: true, message: 'Please enter Gallabox API Key' }]}
          >
            <Input.Password placeholder="Enter your Gallabox API Key" />
          </Form.Item>

          <Form.Item
            label="Gallabox API Secret"
            name="gallaboxApiSecret"
            rules={[{ required: true, message: 'Please enter Gallabox API Secret' }]}
          >
            <Input.Password placeholder="Enter your Gallabox API Secret" />
          </Form.Item>

          <Form.Item
            label="Gallabox WhatsApp URL"
            name="gallaboxWhatsappUrl"
            rules={[
              { required: true, message: 'Please enter Gallabox WhatsApp URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="e.g., https://api.gallabox.com/..." />
          </Form.Item>

          <Divider>Webhook URLs</Divider>

          <Form.Item
            label="User Response Webhook URL"
            name="userResponseWebhookUrl"
            rules={[
              { required: true, message: 'Please enter User Response Webhook URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="e.g., https://yourapp.com/webhooks/user-response" />
          </Form.Item>

          <Form.Item
            label="Lead Webhook URL"
            name="leadWebhookUrl"
            rules={[
              { required: true, message: 'Please enter Lead Webhook URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="e.g., https://yourapp.com/webhooks/lead" />
          </Form.Item>

          <Divider>WhatsApp Configuration</Divider>

          <Form.Item
            label="WhatsApp Channel ID"
            name="whatsappChannelId"
            rules={[{ required: true, message: 'Please enter WhatsApp Channel ID' }]}
          >
            <Input placeholder="Enter your WhatsApp Channel ID" />
          </Form.Item>

          <Form.Item
            label="Introduction Template ID"
            name="introductionTemplateId"
            rules={[{ required: true, message: 'Please enter Introduction Template ID' }]}
          >
            <Input placeholder="Enter your Introduction Template ID" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                {isEditing ? 'Update Settings' : 'Save Settings'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsAppSettingsForm;
